"use client";

import { FC, useState, useRef, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { logoutAction } from "@/actions/logout";

const NAV_ITEMS = [
  { name: "pools", path: "/pools" },
  { name: "tokens", path: "/tokens" },
  { name: "swap", path: "/swap" },
] as const;

const DEMO_ITEMS = [
  { name: "freighter", path: "/tests/demo" },
  { name: "passkeys", path: "/tests/demo-passkeys" },
  { name: "contracts", path: "/tests/contracts" },
] as const;

const Navbar: FC = () => {
  const { data: session } = useSession();
  const pathname = usePathname();
  const isLoggedIn = !!session?.user.accessToken;

  const [demoOpen, setDemoOpen] = useState(false);
  const demoRef = useRef<HTMLDivElement>(null);
  const isDemoActive = pathname.startsWith("/tests/");

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (demoRef.current && !demoRef.current.contains(e.target as Node)) {
        setDemoOpen(false);
      }
    }
    if (demoOpen) document.addEventListener("mousedown", handleClick);
    return () => { document.removeEventListener("mousedown", handleClick); };
  }, [demoOpen]);

  useEffect(() => { setDemoOpen(false); }, [pathname]);

  return (
    <nav className="navbar">
      <div className="px-7 max-[600px]:px-4 h-full flex items-center justify-between">
        {/* Left: Logo */}
        <Link href="/" className="flex items-center gap-2 select-none">
          <Image
            src="/hoops-green.svg"
            alt="Hoops Finance"
            width={24}
            height={24}
            priority
          />
          <span className="text-[11px] tracking-[0.2em] text-[var(--text-secondary)] font-medium hidden sm:inline">
            hoops finance
          </span>
        </Link>

        {/* Center: Tab navigation */}
        <div className="flex items-center gap-1">
          {NAV_ITEMS.map((item) => {
            const isActive = pathname === item.path || pathname.startsWith(item.path + "/");
            return (
              <Link
                key={item.path}
                href={item.path}
                className={[
                  "relative px-4 py-1.5 text-[11px] tracking-[0.15em] font-medium transition-colors max-[600px]:px-2",
                  isActive
                    ? "text-[var(--accent)]"
                    : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
                ].join(" ")}
              >
                {item.name}
                {isActive && (
                  <span
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-[2px]"
                    style={{ background: "var(--accent)" }}
                  />
                )}
              </Link>
            );
          })}

          <span className="w-px h-3 mx-1" style={{ background: "var(--border-default)" }} />

          {/* Demo dropdown */}
          <div ref={demoRef} className="relative">
            <button
              onClick={() => { setDemoOpen((v) => !v); }}
              className={[
                "relative px-4 py-1.5 text-[11px] tracking-[0.15em] font-medium transition-colors max-[600px]:px-2 inline-flex items-center gap-1",
                isDemoActive
                  ? "text-[var(--accent)]"
                  : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
              ].join(" ")}
            >
              demo
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={`transition-transform duration-150 ${demoOpen ? "rotate-180" : ""}`}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
              {isDemoActive && (
                <span
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-[2px]"
                  style={{ background: "var(--accent)" }}
                />
              )}
            </button>

            {demoOpen && (
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 mt-[10px] py-1 rounded-[var(--r)] min-w-[130px] z-50"
                style={{
                  background: "var(--surface-hi)",
                  border: "1px solid var(--border-subtle)",
                }}
              >
                {DEMO_ITEMS.map((item) => {
                  const isActive = pathname === item.path || pathname.startsWith(item.path + "/");
                  return (
                    <Link
                      key={item.path}
                      href={item.path}
                      className={[
                        "block px-4 py-[6px] text-[10px] tracking-[0.1em] font-medium transition-colors",
                        isActive
                          ? "text-[var(--accent)]"
                          : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
                      ].join(" ")}
                    >
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right: Login/Logout */}
        <div className="flex items-center">
          {isLoggedIn ? (
            <form action={logoutAction}>
              <button
                type="submit"
                className="text-[11px] tracking-[0.15em] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
              >
                log out
              </button>
            </form>
          ) : (
            <Link
              href="/auth"
              className="inline-flex items-center h-8 px-4 text-[11px] tracking-[0.1em] font-semibold rounded-[var(--r)] transition-all duration-200"
              style={{
                background: "var(--accent)",
                color: "var(--bg)",
              }}
            >
              start earning
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
